// Cliente HTTP. Anexa o JWT, trata 401 derrubando a sessão local (é assim que
// o front desloga sozinho quando o usuário é desativado no backend) e converte
// erro da API em Error com a mensagem que veio do servidor.

const TOKEN_KEY = 'c1hd.token'

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* modo privado: segue sem persistir */
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type SessionExpiredHandler = () => void
let onSessionExpired: SessionExpiredHandler = () => {}
export function setSessionExpiredHandler(fn: SessionExpiredHandler): void {
  onSessionExpired = fn
}

/**
 * Rotas em que 401 quer dizer "credencial errada", não "sessão acabou".
 * Quem entra com a senha trocada precisa ler "E-mail ou senha incorretos", e
 * não ser avisado de uma sessão que nunca existiu.
 */
const CREDENTIAL_PATHS = ['/auth/login', '/auth/forgot-password', '/auth/reset-password']

function isCredentialPath(path: string): boolean {
  return CREDENTIAL_PATHS.some((p) => path.startsWith(p))
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`/api${path}`, { ...init, headers })

  // Numa rota protegida, 401 significa que não há acesso: limpa a sessão e o
  // front volta para o login. Já nas rotas de credencial o 401 é a resposta
  // normal para senha errada, e a mensagem do servidor é que vale.
  if (res.status === 401 && !isCredentialPath(path)) {
    setToken(null)
    onSessionExpired()
    throw new ApiError(
      401,
      token
        ? 'Sua sessão expirou. Entre novamente.'
        : 'Você precisa entrar para ver esta página.',
    )
  }

  if (res.status === 204) return undefined as T

  const payload = (await res.json().catch(() => null)) as
    { error?: string; details?: unknown } | T | null

  if (!res.ok) {
    const err = payload as { error?: string; details?: unknown } | null
    throw new ApiError(
      res.status,
      err?.error ?? 'Não foi possível concluir a ação',
      err?.details,
    )
  }
  return payload as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: data ? JSON.stringify(data) : undefined }),
  put: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'PUT', body: data ? JSON.stringify(data) : undefined }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/** Monta a query string do contrato de listagem (paginação e ordenação).
 *  `append` serve para quando o caminho já carrega parâmetros próprios. */
export function listParams(
  p: {
    page: number
    size: number
    sort: string
    dir: 'asc' | 'desc'
    q?: string
  },
  append = false,
): string {
  const sp = new URLSearchParams({
    page: String(p.page),
    size: String(p.size),
    sort: p.sort,
    dir: p.dir,
  })
  if (p.q) sp.set('q', p.q)
  return `${append ? '&' : '?'}${sp.toString()}`
}
